import type { IFiscalTaxAmounts } from './fiscal';
import type { IFiscalSummaryTotals } from '@utils/cart-fiscal.util';
import type { IAddress } from './address';
import type { IClient } from './client';
import type { IProduct } from './product';

export enum ESellerOrderStep {
	BUILD = 0,
	FINALIZE = 1,
}

export enum ESellerOrderStatus {
	DRAFT = 'draft',
	SUBMITTED = 'submitted',
}

export interface ISellerOrderAddItemOptions {
	quantity?: number;
	unitPrice?: number;
	originalPrice?: number;
	minimumPrice?: number;
	shipmentPrice?: number;
	priceListId?: number;
	priceList?: string;
	packingId?: number | null;
	packingQuantity?: number | null;
	packingLabel?: string;
	variationLabel?: string;
}

export interface ISellerOrderItem extends IFiscalTaxAmounts {
	id?: number;
	productId: number;
	product: IProduct;
	quantity: number;
	sellingUnit?: string;
	/** Table / list price before the seller edits the unit. */
	originalPrice: number;
	/** Unit price the seller is charging, before discount %. Floored at minimumPrice. */
	unitPrice: number;
	discount: number;
	discountPercentage: number;
	/** unitPrice after discountPercentage. */
	unitPriceFinal: number;
	minimumPrice: number;
	shipmentPrice: number;
	/** (unitPriceFinal - originalPrice) * quantity. AFV flexBalance. */
	flexBalance: number;
	priceListId?: number;
	priceList?: string;
	storeId?: number;
	storehouseId?: number;
	packingId?: number | null;
	packingQuantity?: number | null;
	packingLabel?: string;
	variationLabel?: string;
	subtotal: number;
}

export interface ISellerOrderShipment {
	shippingMethodId?: number;
	shippingMethod?: string;
	shippingTypeId?: number;
	shippingType?: string;
	addressId?: number;
	address?: IAddress;
	obs?: string;
}

export interface ISellerOrderPayment {
	paymentMethodId?: number;
	paymentMethod?: string;
	paymentTermId?: number;
	paymentTerm?: string;
	orderTypeId?: number;
	orderType?: string;
}

export interface ISellerOrder {
	id?: number;
	clientId?: number;
	client?: IClient;
	items: ISellerOrderItem[];
	shipment: ISellerOrderShipment;
	payment: ISellerOrderPayment;
	subtotal: number;
	totalDiscount: number;
	total: number;
	status: ESellerOrderStatus;
	obs?: string;
	sellerId?: number;
	/** Selected at order start (AFV loja). */
	storeId?: number;
	store?: string;
	/** AFV depósito — used for stock checks (may differ from storeId). */
	storehouseId?: number;
	priceListId?: number;
	priceList?: string;
	/** AFV: flex is generated only when the selected order type allows it. */
	generatesFlexBalance?: boolean;
	isDevolution?: boolean;
	isQuote?: boolean;
	expiresAt?: string;
	/** Order-level discount, applied to every item. */
	discountPercentage?: number;
	discount?: number;
	/** Order-level freight, split across items by quantity. */
	shipmentPrice?: number;
	createdAt?: Date;
	updatedAt?: Date;
}

/** One draft per user. */
export interface ISellerOrderDraft {
	id: number;
	clientName?: string;
	total: number;
	itemCount: number;
	updatedAt: Date | string;
}

export interface ISellerOrderResumeResult {
	applied: boolean;
	warnings: string[];
	order?: ISellerOrder;
}

/** Sent on afv/orders only when fiscalEngine is on. */
export interface ISellerOrderFiscalSubmit {
	sellingOperationId?: number | null;
	storeId?: number | null;
}

/** Screen totals. order.total stays the merchandise amount. */
export interface ISellerOrderFiscalView {
	enabled: boolean;
	calculating: boolean;
	showSummary: boolean;
	informational: boolean;
	totals: IFiscalSummaryTotals;
	warnings: string[];
	displayTotal: number;
}

export interface ISellerOrderChecklist {
	hasClient: boolean;
	hasItems: boolean;
	hasPaymentCondition: boolean;
	hasShippingMethod: boolean;
}
